/**
 * 本地草稿持久化（localStorage）
 * 目标：刷新页面不丢失“标书解析/目录/正文”关键中间结果，方便调试与续写。
 *
 * 注意：localStorage 容量有限（通常 5-10MB），大正文可能触发 QUOTA_EXCEEDED_ERR。
 * 这里做了 try/catch，失败时不影响主流程。
 */

import type { AppState, OutlineItem } from '../types';

let activeMemberId: number | null = null;
let activeProjectScope: number | 'legacy' | 'new' = 'legacy';

const getStorageKeys = () => {
  if (activeMemberId === null) {
    return null;
  }

  const prefix = `bid:aibiaoshu:user:${activeMemberId}:project:${activeProjectScope}`;
  return {
    draft: `${prefix}:draft:v2`,
    contentById: `${prefix}:contentById:v2`,
  };
};

export type DraftState = Pick<
  AppState,
  'currentStep' | 'fileContent' | 'projectOverview' | 'techRequirements' | 'outlineData'
> & {
  lastEditedChapterId?: string;
  contentScrollTop?: number;
  failedChapterIds?: string[];
};

export type ContentById = Record<string, string>; // 章节id -> content

const safeJsonParse = <T,>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

export const draftStorage = {
  setMemberId(memberId: number | null) {
    activeMemberId = memberId;
    if (memberId === null) {
      activeProjectScope = 'legacy';
    }
  },

  setProjectId(projectId: number | null) {
    activeProjectScope = projectId ?? 'new';
  },

  migrateToProject(projectId: number) {
    const previousKeys = getStorageKeys();
    activeProjectScope = projectId;
    const nextKeys = getStorageKeys();

    if (!previousKeys || !nextKeys || previousKeys.draft === nextKeys.draft) {
      return;
    }

    try {
      const draft = localStorage.getItem(previousKeys.draft);
      const content = localStorage.getItem(previousKeys.contentById);
      if (draft && !localStorage.getItem(nextKeys.draft)) {
        localStorage.setItem(nextKeys.draft, draft);
      }
      if (content && !localStorage.getItem(nextKeys.contentById)) {
        localStorage.setItem(nextKeys.contentById, content);
      }
      localStorage.removeItem(previousKeys.draft);
      localStorage.removeItem(previousKeys.contentById);
    } catch (e) {
      console.warn('迁移项目草稿失败:', e);
    }
  },

  loadDraft(): Partial<DraftState> | null {
    const keys = getStorageKeys();
    return keys
      ? safeJsonParse<Partial<DraftState>>(localStorage.getItem(keys.draft))
      : null;
  },

  saveDraft(partial: Partial<DraftState>) {
    try {
      const keys = getStorageKeys();
      if (!keys) return;

      const prev = safeJsonParse<Partial<DraftState>>(localStorage.getItem(keys.draft)) || {};
      const next = { ...prev, ...partial };
      localStorage.setItem(keys.draft, JSON.stringify(next));
    } catch (e) {
      console.warn('保存草稿失败（可能是 localStorage 空间不足）:', e);
    }
  },

  clearAll() {
    try {
      const keys = getStorageKeys();
      if (!keys) return;

      localStorage.removeItem(keys.draft);
      localStorage.removeItem(keys.contentById);
    } catch (e) {
      console.warn('清空 localStorage 失败:', e);
    }
  },

  loadContentById(): ContentById {
    const keys = getStorageKeys();
    return keys
      ? safeJsonParse<ContentById>(localStorage.getItem(keys.contentById)) || {}
      : {};
  },

  saveContentById(contentById: ContentById) {
    try {
      const keys = getStorageKeys();
      if (!keys) return;

      localStorage.setItem(keys.contentById, JSON.stringify(contentById));
    } catch (e) {
      console.warn('保存正文内容失败（可能是 localStorage 空间不足）:', e);
    }
  },

  upsertChapterContent(chapterId: string, content: string) {
    try {
      const map = draftStorage.loadContentById();
      map[chapterId] = content;
      draftStorage.saveContentById(map);
    } catch (e) {
      console.warn('保存章节内容失败:', e);
    }
  },

  /**
   * 按当前 outline 的叶子节点过滤 contentById，避免目录变更后错误回填。
   */
  filterContentByOutlineLeaves(outline: OutlineItem[]): ContentById {
    const map = draftStorage.loadContentById();
    const leafIds = new Set<string>();
    const walk = (items: OutlineItem[]) => {
      items.forEach((it) => {
        if (!it.children || it.children.length === 0) {
          leafIds.add(it.id);
          return;
        }
        walk(it.children);
      });
    };
    walk(outline);

    const filtered: ContentById = {};
    Object.keys(map).forEach((id) => {
      if (leafIds.has(id)) filtered[id] = map[id];
    });
    return filtered;
  },
};
